import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./YoursPaywall.module.scss"

// ─────────────────────────────────────────────────────────────────────────────
// "Yours" — an ONBOARDING paywall built as a CLAIM OF OWNERSHIP. A brand-new
// user has just created their vault; instead of selling protection through fear
// or an object-to-admire, this screen asks them to do one small, personal thing:
// write their name on it. The mechanic is a TYPED TEXT INPUT (input-driven — the
// axis the divergence ledger flags as least explored), not a scrub/hold/flip/
// dial/pour/press. As you type, your first memories are inscribed to you one by
// one and a monogram medallion presses in: "The private library of ‹you›."
//
// Behavioural spine: ENDOWMENT + the name-letter/ownership effect + COMMITMENT/
// CONSISTENCY + FRESH-START. Writing your own name on a thing makes it yours in
// a way a feature list never will — and it's honest: the payoff really is that
// only you can open it. Emotional register: personal, warm, quietly ceremonious
// — a signing, not a warning. Directive for this run is "focus on security and
// privacy", so the value frame is only-you access — but reached through
// belonging/ownership rather than the intrusion-fear or lock-hardware of the
// security paywalls before it.
//
// Honest & App-Store compliant: yearly is the featured, most prominent price
// (7-day free trial, per-month broken out, real Save %); monthly is a small
// secondary line. No countdown, no scarcity, no data-loss claim — your photos
// are always yours to keep; your name only marks the vault as yours to open.
// Nothing is reused from another paywall but the house fonts, colour tokens and
// bundled memory photos. Self-contained: renders on debug data, no injection.
// ─────────────────────────────────────────────────────────────────────────────

const PUB = process.env.PUBLIC_URL ?? ""
// Reuse the bundled everyday-memory photos — no runtime egress.
const photo = (n: string) => `${PUB}/byheart/${n}.jpg`

// The user's first memories, as a small gallery wall. Order/rotation give it the
// feel of a hand-arranged shelf rather than a database grid.
const WALL: { src: string; tall?: boolean; tilt: number }[] = [
  { src: photo("m02"), tilt: -2.4 },
  { src: photo("m07"), tall: true, tilt: 1.6 },
  { src: photo("m05"), tilt: 2.2 },
  { src: photo("m13"), tilt: -1.4 },
  { src: photo("m10"), tilt: 1.8 },
  { src: photo("m16"), tall: true, tilt: -2.0 },
  { src: photo("m08"), tilt: 1.2 },
  { src: photo("m04"), tilt: -1.8 },
]
const TOTAL = WALL.length

const SAMPLE = "Alex"

// How many memories are inscribed for a given name length. First keystroke
// seats the first memory; the wall is fully claimed a little before the name is
// finished, so completing the name feels like arriving, not grinding.
function sealedFor(len: number): number {
  if (len <= 0) return 0
  return Math.min(TOTAL, Math.ceil((len / 4) * TOTAL) || 1)
}

function monogramOf(name: string): string {
  const first = name.trim().charAt(0)
  return first ? first.toUpperCase() : ""
}

function YoursPaywall() {
  const { state } = useAppState()
  const reduce = useReducedMotion()

  const topInset = state.initialData?.topInset ?? 0
  const bottomInset = state.initialData?.bottomInset ?? 0

  const premium = state.productData?.premium
  const yearly = premium?.yearlyProduct
  const monthly = premium?.monthlyProduct
  const locale = yearly?.priceLocale

  const yearlyPrice = yearly ? formatCurrency(yearly.price, locale) : ""
  const perMonth = yearly ? formatCurrency(yearly.price / 12, locale) : ""
  const monthlyPrice = monthly ? formatCurrency(monthly.price, locale) : ""
  const savings = useMemo(() => {
    if (!yearly || !monthly) return 0
    return Math.round((1 - yearly.price / (monthly.price * 12)) * 100)
  }, [yearly, monthly])

  const [name, setName] = useState(reduce ? SAMPLE : "")
  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const [showClose, setShowClose] = useState(false)
  const [committed, setCommitted] = useState(false) // CTA pressed
  const [typing, setTyping] = useState(false) // auto-demo running
  const [userTouched, setUserTouched] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const timers = useRef<number[]>([])
  // A ref so the auto-type loop can see the latest "user took over" flag without
  // being torn down and restarted on every keystroke.
  const userTouchedRef = useRef(false)
  useEffect(() => {
    userTouchedRef.current = userTouched
  }, [userTouched])

  const push = (fn: () => void, ms: number) =>
    timers.current.push(window.setTimeout(fn, ms))

  // On load: auto-inscribe a sample name so the screen reads as "claimed" for
  // passive viewers and screenshots — then hand the field over. If the user has
  // already started typing, never fight them.
  useEffect(() => {
    push(() => setShowClose(true), reduce ? 0 : 1500)
    if (reduce) return () => timers.current.forEach(window.clearTimeout)

    setTyping(true)
    let i = 0
    const step = () => {
      setName((cur) => {
        // bail out the instant a real user takes over
        if (userTouchedRef.current) return cur
        return SAMPLE.slice(0, i + 1)
      })
      i += 1
      if (i < SAMPLE.length && !userTouchedRef.current) {
        push(step, 230)
      } else {
        setTyping(false)
      }
    }
    push(step, 900)

    return () => timers.current.forEach(window.clearTimeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduce])

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!userTouched) setUserTouched(true)
    setTyping(false)
    // keep it tidy: letters, spaces, common name marks; cap the length
    const cleaned = e.target.value.replace(/[^\p{L}\p{M}'’.\- ]/gu, "")
    setName(cleaned.slice(0, 22))
  }

  const takeOver = () => {
    setUserTouched(true)
    setTyping(false)
    setName("")
    push(() => inputRef.current?.focus(), 20)
  }

  const useSample = () => {
    setUserTouched(true)
    setTyping(false)
    setName(SAMPLE)
  }

  const purchase = useCallback(() => {
    const product = plan === "yearly" ? yearly : monthly
    setCommitted(true)
    push(() => {
      if (product) native.subscribe(product)
    }, 640)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, yearly, monthly])

  const handleClose = () => native.postNativeEvent("close")

  const trimmed = name.trim()
  const claimed = trimmed.length > 0
  const sealed = sealedFor(trimmed.length)
  const mono = monogramOf(name)
  const displayName = claimed ? name : ""

  const ctaSub =
    plan === "yearly"
      ? `${yearlyPrice}/year after your 7-day free trial — just ${perMonth}/mo. Renews yearly, cancel anytime.`
      : `${monthlyPrice}/month. Renews monthly, cancel anytime.`

  return (
    <div
      className={`${styles.root} ${claimed ? styles.isClaimed : ""} ${
        committed ? styles.isCommitted : ""
      }`}
    >
      <div className={styles.paper} aria-hidden>
        <span className={styles.glowWarm} />
        <span className={styles.glowBlue} />
        <span className={styles.grain} />
      </div>

      <div
        className={styles.scroll}
        style={{ paddingTop: topInset + 10, paddingBottom: bottomInset + 14 }}
      >
        {/* Top bar — brand clarity */}
        <div className={styles.topbar}>
          <span className={styles.wordmark}>
            <span className={styles.wmMark} aria-hidden>
              <svg viewBox="0 0 24 24" width="12" height="12">
                <path
                  d="M8 10V7a4 4 0 0 1 8 0v3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <rect
                  x="5.5"
                  y="10"
                  width="13"
                  height="9.5"
                  rx="2.4"
                  fill="currentColor"
                />
              </svg>
            </span>
            PRIVATE&nbsp;PHOTO&nbsp;VAULT
          </span>
          <button
            className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
            onClick={handleClose}
            aria-label="Not now"
            tabIndex={showClose ? 0 : -1}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Headline */}
        <header className={styles.hero}>
          <p className={styles.eyebrow}>Your new vault</p>
          <h1 className={styles.title}>
            Put your name on it.
            <br />
            <span className={styles.titleThen}>Only you can open it.</span>
          </h1>
        </header>

        {/* ── The claim: a gallery wall + a nameplate you sign ─────────────── */}
        <section className={styles.plate} aria-label="Make this vault yours">
          {/* monogram wax medallion */}
          <span
            className={`${styles.mono} ${claimed ? styles.monoOn : ""}`}
            aria-hidden
          >
            <span className={styles.monoRing} />
            <span className={styles.monoLetter}>{mono || "·"}</span>
          </span>

          {/* the gallery wall of first memories */}
          <div className={styles.wall}>
            {WALL.map((p, i) => {
              const on = i < sealed
              return (
                <span
                  key={i}
                  className={[
                    styles.frame,
                    p.tall ? styles.frameTall : "",
                    on ? styles.frameOn : styles.frameOff,
                  ].join(" ")}
                  style={{
                    ["--tilt" as any]: `${p.tilt}deg`,
                    ["--seat" as any]: `${i * 55}ms`,
                  }}
                >
                  <img src={p.src} alt="" draggable={false} loading="eager" />
                  <span className={styles.mark} aria-hidden>
                    <span className={styles.markOff} />
                    <span className={styles.markOn}>
                      <svg viewBox="0 0 24 24" width="11" height="11">
                        <path
                          d="M5 12.5l4.4 4.4L19 7"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </span>
                </span>
              )
            })}
            <span className={styles.wallFade} aria-hidden />
          </div>

          {/* the nameplate / inscription line */}
          <div className={styles.nameplate}>
            <span className={styles.plateLabel}>The private library of</span>

            <label
              className={`${styles.field} ${typing ? styles.fieldTyping : ""}`}
            >
              <span className={styles.fieldInk} aria-hidden>
                {displayName || (
                  <span className={styles.placeholder}>your name</span>
                )}
                <span className={styles.caret} />
              </span>
              <input
                ref={inputRef}
                className={styles.input}
                value={name}
                onChange={onChange}
                onFocus={() => setUserTouched(true)}
                spellCheck={false}
                autoComplete="off"
                autoCapitalize="words"
                aria-label="Write your name on your vault"
                enterKeyHint="done"
              />
              <span className={styles.rule} />
            </label>

            <div className={styles.plateFoot}>
              <span
                className={`${styles.registered} ${
                  claimed ? styles.registeredOn : ""
                }`}
              >
                <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden>
                  <path
                    d="M8 10V7a4 4 0 0 1 8 0v3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <rect
                    x="5.5"
                    y="10"
                    width="13"
                    height="9.5"
                    rx="2.2"
                    fill="currentColor"
                  />
                </svg>
                {claimed ? (
                  <>
                    Registered to <b>{trimmed}</b> · only you can open it
                  </>
                ) : (
                  <>Sign it to make it yours</>
                )}
              </span>

              {!userTouched ? (
                <button className={styles.change} onClick={takeOver}>
                  Not you? Write your name
                </button>
              ) : !claimed ? (
                <button className={styles.change} onClick={useSample}>
                  Use a sample name
                </button>
              ) : (
                <span className={styles.kept}>
                  {sealed}/{TOTAL} memories inscribed
                </span>
              )}
            </div>
          </div>
        </section>

        {/* what "yours" actually means — real protections, kept compact */}
        <ul className={styles.assurances} aria-label="What only you get">
          <li>
            <span className={styles.aDot} />
            Opens only with your Face&nbsp;ID or&nbsp;PIN
          </li>
          <li>
            <span className={styles.aDot} />
            Kept out of your camera roll &amp; shared albums
          </li>
          <li>
            <span className={styles.aDot} />
            Private, encrypted backup on every device
          </li>
        </ul>

        {/* ── Plans — yearly is the hero ─────────────────────────────────── */}
        <section className={styles.plans} aria-label="Choose your plan">
          <button
            className={`${styles.plan} ${styles.planYear} ${
              plan === "yearly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            {savings > 0 && (
              <span className={styles.bestTag}>Save {savings}%</span>
            )}
            <span className={styles.planMain}>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
              <span className={styles.planTextWrap}>
                <span className={styles.planName}>Yours for the year</span>
                <span className={styles.planCovers}>
                  7-day free trial, then billed yearly
                </span>
              </span>
              <span className={styles.planPrice}>
                <span className={styles.priceRow}>
                  <span className={styles.priceBig}>{yearlyPrice}</span>
                  <span className={styles.pricePer}>/yr</span>
                </span>
                <span className={styles.pricePm}>{perMonth}/mo</span>
              </span>
            </span>
          </button>

          <button
            className={`${styles.plan} ${styles.planMonth} ${
              plan === "monthly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.planMain}>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
              <span className={styles.planTextWrap}>
                <span className={styles.planNameSm}>Monthly</span>
                <span className={styles.planCoversSm}>one month at a time</span>
              </span>
              <span className={styles.planPriceSm}>
                <span className={styles.priceSm}>{monthlyPrice}</span>
                <span className={styles.pricePerSm}>/mo</span>
              </span>
            </span>
          </button>
        </section>

        {/* CTA */}
        <div className={styles.ctaDock}>
          <button
            className={`${styles.cta} ${committed ? styles.ctaCommitted : ""}`}
            onClick={purchase}
          >
            <span className={styles.ctaLabel}>
              {committed
                ? "Making it yours…"
                : claimed
                  ? `Make it yours, ${trimmed.split(" ")[0]}`
                  : "Make it yours — 7 days free"}
            </span>
            {!committed && (
              <svg
                className={styles.ctaArrow}
                viewBox="0 0 24 24"
                width="18"
                height="18"
                aria-hidden
              >
                <path
                  d="M5 12h13M12 5l7 7-7 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
          <p className={styles.ctaSub}>{ctaSub}</p>
          <p className={styles.reassure}>
            Your photos are always yours to keep. Your name simply marks this
            vault as yours to open — nothing is ever locked away from you or
            deleted.
          </p>

          <div className={styles.legal}>
            <button onClick={() => native.postNativeEvent("already_purchased")}>
              Restore
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("terms")}>
              Terms
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("privacy")}>
              Privacy
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default YoursPaywall
